(function(){
  'use strict';

  if (window.__ganarMasAssetLoaded) {
    if (typeof window.__initGanarMas === 'function') window.__initGanarMas();
    return;
  }

  window.__ganarMasAssetLoaded = true;

  function safeLog() {
    try {
      console.log.apply(console, ['[GANAMAS]'].concat(Array.prototype.slice.call(arguments)));
    } catch(e) {}
  }

  function parseConfig(root) {
    const configEl = root.querySelector('[data-ganamas-config]');
    if (!configEl) return {};

    try {
      return JSON.parse(configEl.textContent || '{}');
    } catch (e) {
      console.warn('[GANAMAS] Invalid config JSON', e);
      return {};
    }
  }

  function resolveCartForm(root) {
    const closestForm = root.closest('form.cart');
    if (closestForm) return closestForm;

    const drawer = root.closest('[data-drawer="drawer-cart"]');
    if (drawer) return drawer.querySelector('form.cart');

    return null;
  }

  function resolveQuickOrderCustomerId(config) {
    const associateInput = document.querySelector('[purchase-order-associate]');
    return (associateInput && associateInput.value) || config.customerId || null;
  }

  function interpolateMessage(template, values) {
    return String(template || '')
      .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, function(_, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : '';
      })
      .replace(/__([a-zA-Z0-9_]+)__/g, function(_, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : '';
      });
  }

  function formatCurrency(value, currencyCode) {
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    const currency = String(currencyCode || '').trim();
    const formatted = '$' + n.toLocaleString('en-US', {
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
    if (currency) return `${formatted} ${currency}`;

    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
    } catch(e) {
      return '$' + n;
    }
  }

  // Semana Ganar Más: hora Ecuador (UTC-5, sin horario de verano), de sábado a
  // sábado, sin depender de la zona horaria del navegador. Mismo cálculo que
  // betterware-ec-backend (ganarMasWeek.ts), bwec-backend y bwec-widgets.
  const GANAR_MAS_UTC_OFFSET_HOURS = -5;
  const MS_PER_DAY = 86400000;

  function ecuadorDateOnly(inputDate) {
    const shifted = new Date(inputDate.getTime() + GANAR_MAS_UTC_OFFSET_HOURS * 3600000);
    return new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate()));
  }

  function startOfWeekSaturday(inputDate) {
    const offset = (inputDate.getUTCDay() - 6 + 7) % 7;
    return new Date(inputDate.getTime() - offset * MS_PER_DAY);
  }

  function startOfWeek1Saturday(year) {
    return startOfWeekSaturday(new Date(Date.UTC(year, 0, 1)));
  }

  function getCurrentWeek(inputDate) {
    const t = ecuadorDateOnly(inputDate);
    const start = startOfWeekSaturday(t);
    let year = t.getUTCFullYear();
    let startYear = startOfWeek1Saturday(year);

    if (start < startYear) {
      year -= 1;
      startYear = startOfWeek1Saturday(year);
    } else {
      const startNextYear = startOfWeek1Saturday(year + 1);
      if (!(start < startNextYear)) {
        year += 1;
        startYear = startOfWeek1Saturday(year);
      }
    }

    const diffDays = Math.floor((start - startYear) / MS_PER_DAY);
    const week = Math.floor(diffDays / 7) + 1;
    const end = new Date(start.getTime() + 6 * MS_PER_DAY);

    return { week, year, start, end };
  }

  function extractNumericId(gid) {
    if (!gid) return '';
    const parts = String(gid).split('/');
    return parts[parts.length - 1] || '';
  }

  const productTagsCache = new Map();
  let cartValidationQueue = Promise.resolve();
  let backgroundValidation = null;
  let backgroundValidationQueued = false;

  function isRegaloItem(item) {
    return String(item?.properties?._ganar_mas_regalo) === 'true';
  }

  function isBenefitItem(item) {
    return String(item?.properties?._ganar_mas) === 'true' || isRegaloItem(item);
  }

  function hasBenefitMetadata(item) {
    const props = item.properties || {};
    return isBenefitItem(item) &&
      !(isRegaloItem(item) && String(props._ganar_mas) === 'true') &&
      !!String(props._ganar_mas_customer_id || '').trim() &&
      String(props._ganar_mas_product_id) === String(item.product_id) &&
      ['_ganar_mas_level', '_ganar_mas_weeks_with_sales', '_ganar_mas_week', '_ganar_mas_year']
        .every(key => Number.isInteger(Number(props[key])) && Number(props[key]) > 0);
  }

  async function cartRequest(path, options) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + path, {
      ...options,
      signal: AbortSignal.timeout(20000)
    });
    if (!response.ok) {
      const error = new Error(`Ganar Mas cart validation failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  async function getCartProductTags(item) {
    if (!item.handle) throw new Error('Cart product handle is missing');
    if (!productTagsCache.has(item.handle)) {
      productTagsCache.set(item.handle, cartRequest(`products/${encodeURIComponent(item.handle)}.js`)
        .then(product => {
          if (!Array.isArray(product.tags)) throw new Error('Cart product tags are missing');
          return product.tags;
        })
        .catch(error => {
          productTagsCache.delete(item.handle);
          // Producto no visible en la tienda (404): no se puede leer su etiqueta,
          // pero no debe bloquear el checkout ni quitar nada del carrito.
          if (error && error.status === 404) return [];
          throw error;
        }));
    }
    return productTagsCache.get(item.handle);
  }

  async function sanitizeGanarMasCart(options = {}) {
    const run = async () => {
      let cart = await cartRequest('cart.js');
      const items = cart.items || [];
      const unauthorized = new Set();
      await Promise.all(items.filter(item => !isBenefitItem(item)).map(async item => {
        const tags = await getCartProductTags(item);
        if (tags.some(tag => /^ganar?-mas/i.test(String(tag).trim()))) unauthorized.add(item.key);
      }));

      const validItems = items.filter(item => hasBenefitMetadata(item) &&
        (options.customerId === undefined ||
          String(item.properties._ganar_mas_customer_id) === String(options.customerId)));

      // Por cliente: un principal y sus regalos (una línea por producto regalo).
      // En Quick Order el carrito puede llevar el beneficio de otro asociado y no
      // se le debe quitar.
      // options.regalos: undefined = no se valida vigencia (se conservan los regalos
      // ligados); [] = no hay regalos vigentes; [productId, ...] = regalos que corresponden.
      const allowedRegalos = Array.isArray(options.regalos)
        ? new Set(options.regalos.map(id => String(id)))
        : null;
      const keptItems = new Set();
      const customerIds = new Set(validItems.map(item => String(item.properties._ganar_mas_customer_id)));
      customerIds.forEach(customerId => {
        const customerItems = validItems.filter(item => String(item.properties._ganar_mas_customer_id) === customerId);
        const primary = customerItems.find(item => !isRegaloItem(item) &&
          (!options.allowPrimary || options.allowPrimary(item)));
        if (!primary) return;
        keptItems.add(primary);

        const keptRegaloProducts = new Set();
        customerItems.forEach(item => {
          if (!isRegaloItem(item)) return;
          const props = item.properties;
          if (String(props._ganar_mas_regalo_of) !== String(primary.product_id)) return;
          if (!['_ganar_mas_customer_id', '_ganar_mas_level', '_ganar_mas_weeks_with_sales', '_ganar_mas_week', '_ganar_mas_year']
            .every(key => String(props[key]) === String(primary.properties[key]))) return;
          const regaloProductId = String(item.product_id);
          if (allowedRegalos && !allowedRegalos.has(regaloProductId)) return;
          if (keptRegaloProducts.has(regaloProductId)) return;
          keptRegaloProducts.add(regaloProductId);
          keptItems.add(item);
        });
      });

      // El beneficio acompaña una compra: sin productos normales en el carrito no
      // puede quedar solo el Ganar Más con su regalo.
      if (!items.some(item => !isBenefitItem(item) && !unauthorized.has(item.key))) keptItems.clear();

      const updates = {};
      for (const item of items) {
        if (unauthorized.has(item.key)) {
          updates[item.key] = 0;
        } else if (isBenefitItem(item)) {
          const quantity = keptItems.has(item) ? 1 : 0;
          if (item.quantity !== quantity) updates[item.key] = quantity;
        }
      }

      if (Object.keys(updates).length) {
        cart = await cartRequest('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        const detail = { cart, ganarMasValidated: true };
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { detail, bubbles: true }));
        document.dispatchEvent(new CustomEvent('ganarMas:cartSynced', { detail, bubbles: true }));
      }
      return cart;
    };
    const result = cartValidationQueue.then(run);
    cartValidationQueue = result.catch(() => {});
    return result;
  }

  window.__sanitizeGanarMasCart = sanitizeGanarMasCart;

  function validateCartInBackground(event) {
    if (event?.detail?.ganarMasValidated) return;
    if (backgroundValidation) {
      backgroundValidationQueued = true;
      return;
    }
    backgroundValidation = (async () => {
      do {
        backgroundValidationQueued = false;
        await sanitizeGanarMasCart();
      } while (backgroundValidationQueued);
    })()
      .catch(error => console.error('[GANAMAS] Cart validation failed', error))
      .finally(() => { backgroundValidation = null; });
  }

  ['DOMContentLoaded', 'theme:cart:change', 'theme:cart:reload', 'swym:cart-loaded']
    .forEach(name => document.addEventListener(name, validateCartInBackground));
  if (document.readyState !== 'loading') validateCartInBackground();

  const approvedCheckoutTargets = new WeakSet();
  const pendingCheckoutTargets = new WeakSet();

  async function validateUnboundCheckout(event) {
    const target = event.type === 'submit' ? event.target : event.target.closest?.('[name="checkout"], a[href]');
    if (!target) return;
    if (event.type === 'click' && target.tagName === 'A' &&
      !/\/checkout\/?$/.test(new URL(target.href, window.location.href).pathname)) return;
    if (event.type === 'submit' && event.submitter?.name !== 'checkout' &&
      !target.querySelector('input[name="checkout"]')) return;
    const form = event.type === 'submit' ? target : target.closest('form');
    if (form?.__ganamasSubmitBound && target.tagName !== 'A') return;
    if (approvedCheckoutTargets.has(target)) {
      approvedCheckoutTargets.delete(target);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendingCheckoutTargets.has(target)) return;
    pendingCheckoutTargets.add(target);
    try {
      if (target.tagName === 'A') {
        const checkoutForm = form?.__ganarMasCheckout ? form :
          Array.from(document.querySelectorAll('form.cart')).find(candidate => candidate.__ganarMasCheckout);
        if (checkoutForm) {
          await checkoutForm.__ganarMasCheckout(event);
          return;
        }
      }
      // Sin el flujo del popup no se recalcula elegibilidad: se conserva la pareja
      // válida (como antes) y solo se limpian duplicados, huérfanos y productos
      // Ganar Más sin propiedades del beneficio.
      await window.RegalonCheckout?.prepare();
      await sanitizeGanarMasCart();
      approvedCheckoutTargets.add(target);
      if (event.type === 'submit') target.requestSubmit(event.submitter || undefined);
      else target.click();
    } catch (error) {
      console.error('[GANAMAS] Checkout validation failed', error);
      alert('Ocurrió un error actualizando tu producto Ganar Más, intenta nuevamente.');
    } finally {
      approvedCheckoutTargets.delete(target);
      pendingCheckoutTargets.delete(target);
    }
  }

  document.addEventListener('click', validateUnboundCheckout, { capture: true });
  document.addEventListener('submit', validateUnboundCheckout, { capture: true });

  function initRoot(root) {
    if (!root || root.__ganarMasInitialized) return;

    const config = parseConfig(root);
    const isQuickOrder = !!config.isQuickOrder;
    const form = isQuickOrder ? null : resolveCartForm(root);
    const modal = root.querySelector('[data-ganamas-modal]');

    if (!modal) return;
    if (!isQuickOrder && !form) return;

    root.__ganarMasInitialized = true;

    const messages = config.messages || {};
    const message = function(key, fallback) {
      return typeof messages[key] !== 'undefined' ? messages[key] : (fallback || '');
    };

    let CUSTOMERID = isQuickOrder ? resolveQuickOrderCustomerId(config) : (config.customerId || null);
    const backdrop = modal.querySelector('[data-ganamas-backdrop]');
    const confirmBtn = modal.querySelector('.ganamas-confirm');
    const choices = Array.from(modal.querySelectorAll('.choice-btn'));
    const checkoutButtons = form ? Array.from(form.querySelectorAll('button[name="checkout"], input[name="checkout"], [data-ganamas-checkout]')) : [];
    const checkoutBtn = checkoutButtons.find(button => button.offsetParent !== null) || checkoutButtons[0] || null;
    const listSelector = '.ganamas-list';
    const chooseTitle = modal.querySelector('.ganamas-choose-title');
    const conditionsMessageSelector = modal.querySelector('.ganamas-conditions-subtitle');
    const beneficiaryEl = modal.querySelector('[data-ganamas-beneficiary]');
    const warningTextEl = modal.querySelector('[data-ganamas-warning-text]');
    const giftAmountEl = modal.querySelector('[data-ganamas-gift-amount]');

    let ganamasEligibility = null;
    let activeModalKind = null;
    let bypassSubmit = false;
    let checkoutLoading = false;
    let ganarMasSyncInProgress = false;
    let currentCartAttemptId = '';
    let availableGanarMasProducts = [];
    let availableGanarMasProductsKey = '';
    let availableRegaloProducts = [];

    function setRandomChoiceVisible(isVisible) {
      modal.querySelectorAll('.ganamas-choices button[data-choice="random"]').forEach(button => {
        button.style.display = isVisible ? '' : 'none';
      });
    }

    function formatGiftPrice(value) {
      return formatCurrency(value, message('giftCurrency', 'USD'));
    }

    function updateGiftPriceMessages(ganarArr) {
      const amount = formatGiftPrice(ganarArr?.giftPrice ?? 0);

      if (giftAmountEl) {
        giftAmountEl.textContent = amount;
      }

      if (warningTextEl) {
        warningTextEl.textContent = interpolateMessage(
          message('availability', ''),
          { amount }
        );
      }
    }

    function getAvailableVariant(product) {
      const edges = product?.variants?.edges || [];
      return edges
        .map(edge => edge && edge.node)
        .find(variant => variant && Number(variant.inventoryQuantity) > 0) || null;
    }

    function normalizeAvailableProducts(products) {
      if (!Array.isArray(products)) return [];
      return products
        .map(product => ({
          product,
          variant: getAvailableVariant(product)
        }))
        .filter(item => item.product?.id && item.variant?.id);
    }

    function pickRandomGanarMasProduct() {
      if (!availableGanarMasProducts.length) {
        throw new Error('No Ganar Mas products available');
      }

      return availableGanarMasProducts[Math.floor(Math.random() * availableGanarMasProducts.length)];
    }

    function getBeneficiaryDisplayLabel(customerId) {
      const id = customerId || CUSTOMERID || '';
      if (isQuickOrder) {
        const fromWin = typeof window.GANARMAS_ASSOCIATE_DISPLAY_NAME === 'string' ? window.GANARMAS_ASSOCIATE_DISPLAY_NAME.trim() : '';
        if (fromWin) return fromWin;
        const el = document.querySelector('[data-js-purchase-order-partner-update-success-text]');
        const t = el && el.textContent ? el.textContent.trim() : '';
        return t || `Cliente ${id}`;
      }

      if (config.customerDisplayName) return config.customerDisplayName;
      return `Cliente ${id}`;
    }

    function hasAcceptedPprProduct(ganarArr) {
      return Array.isArray(ganarArr?.selectedProduct) && ganarArr.selectedProduct.length > 0;
    }

    function needsGiftModal(resp, ganarArr) {
      if (!resp || !ganarArr) return false;
      const noSelectedProduct = !hasAcceptedPprProduct(ganarArr);
      const currentSelectionStatus = resp.currentSelectionStatus || 'none';
      return (
        !!resp.eligible &&
        noSelectedProduct &&
        currentSelectionStatus !== 'omitted' &&
        ['none', 'equal'].includes(resp.status) &&
        Number(ganarArr.level) > 0
      );
    }

    function setConditionsMessage() {
      if (!conditionsMessageSelector) return;

      conditionsMessageSelector.innerHTML = isQuickOrder
        ? `<p>${message('associateCondition')}</p>`
        : `<p>${message('base')}</p>`;
    }

    function openGanamasModal() {
      const ganarArr = ganamasEligibility?.ganarMas?.[0] ?? {};
      updateGiftPriceMessages(ganarArr);
      setConditionsMessage();

      choices.forEach(c => c.classList.remove('choice-active'));
      if (chooseTitle) chooseTitle.classList.add('d-none');

      const container = modal.querySelector('.ganamas-list');
      if (container) container.innerHTML = '';

      modal.querySelectorAll('.ganamas-radio').forEach(r => { r.disabled = true; });
      setRandomChoiceVisible(availableGanarMasProducts.length > 0);

      if (confirmBtn) confirmBtn.disabled = true;
      if (beneficiaryEl) beneficiaryEl.textContent = getBeneficiaryDisplayLabel(CUSTOMERID);

      modal.classList.remove('d-none');
      modal.setAttribute('aria-hidden', 'false');
      activeModalKind = 'gift';
      document.body.style.overflow = 'hidden';

      const firstFocusable = modal.querySelector('.ganamas-cancel, .ganamas-confirm');
      if (firstFocusable) firstFocusable.focus();
    }

    async function prepareAndOpenGanamasModal(ganarArr) {
      const weeksWithSales = ganarArr.weeksWithSales;
      const level = ganarArr.level;

      availableGanarMasProducts = [];
      availableGanarMasProductsKey = '';
      availableRegaloProducts = [];
      setRandomChoiceVisible(true);

      try {
        const products = await loadAvailableGanarMasProducts(
          CUSTOMERID,
          level,
          weeksWithSales,
          true
        );
        setRandomChoiceVisible(products.length > 0);
      } catch (err) {
        availableGanarMasProducts = [];
        availableGanarMasProductsKey = '';
        availableRegaloProducts = [];
        setRandomChoiceVisible(false);
      }

      openGanamasModal();
      setCheckoutLoadingState(false);
      if (confirmBtn) confirmBtn.disabled = true;
    }

    function buildGanamasSelectionPayload(status, source) {
      const ganarData = (ganamasEligibility && Array.isArray(ganamasEligibility.ganarMas) && ganamasEligibility.ganarMas.length) ? ganamasEligibility.ganarMas[0] : {};
      const prev = getCurrentWeek(new Date());

      return {
        customerId: `${CUSTOMERID}`,
        productId: '',
        salesAmount: (ganarData && typeof ganarData.salesAmount !== 'undefined') ? Number(Number(ganarData.salesAmount).toFixed(2)) : 0,
        giftPrice: (ganarData && typeof ganarData.giftPrice !== 'undefined') ? ganarData.giftPrice : 0,
        weeksWithSales: (ganarData && typeof ganarData.weeksWithSales !== 'undefined') ? ganarData.weeksWithSales : 0,
        level: (ganarData && typeof ganarData.level !== 'undefined') ? ganarData.level : 0,
        week: prev.week,
        year: prev.year,
        status,
        source
      };
    }

    function closeGanamasModal() {
      modal.classList.add('d-none');
      modal.setAttribute('aria-hidden', 'true');
      if (activeModalKind === 'gift') activeModalKind = null;
      document.body.style.overflow = '';
      removeHiddenCheckout();
    }

    function addHiddenCheckout() {
      if (!form) return;
      if (!form.querySelector('input[name="checkout"]')) {
        const h = document.createElement('input');
        h.type = 'hidden';
        h.name = 'checkout';
        h.value = '1';
        h.setAttribute('data-temporary-checkout', 'true');
        form.appendChild(h);
      }
    }

    function removeHiddenCheckout() {
      if (!form) return;
      const h = form.querySelector('input[name="checkout"][data-temporary-checkout]');
      if (h && h.parentNode) h.parentNode.removeChild(h);
    }

    function setCheckoutLoadingState(isLoading) {
      checkoutLoading = isLoading;
      if (!checkoutBtn) return;

      checkoutBtn.disabled = isLoading;
      checkoutBtn.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      checkoutBtn.classList.toggle('btn--disabled', isLoading);
      checkoutBtn.classList.toggle('btn--loading', isLoading);
    }

    function confirmFlow() {
      setCheckoutLoadingState(true);
      addHiddenCheckout();
      bypassSubmit = true;
      closeGanamasModal();

      try {
        if (isQuickOrder) {
          document.dispatchEvent(new CustomEvent('ganarMas:continueCheckout'));
        } else {
          window.location.href = '/checkout';
        }
      } catch(e) {
        if (isQuickOrder) {
          document.dispatchEvent(new CustomEvent('ganarMas:continueCheckout'));
        } else {
          setTimeout(() => {
            if (!window.location.pathname.includes('/checkout')) {
              bypassSubmit = false;
              window.location.href = '/checkout';
            }
          }, 700);
        }
      }
    }

    async function fetchCartData() {
      const res = await fetch('/cart.js');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Cart fetch failed: ' + res.status + ' - ' + txt);
      }
      return await res.json();
    }

    function isGanarMasCartItem(item) {
      const props = item?.properties || {};
      return props._ganar_mas === 'true' || props._ganar_mas === true || props._ganar_mas_regalo === 'true' || props._ganar_mas_regalo === true;
    }

    function getGanarMasCartItemCustomerId(item) {
      const props = item?.properties || {};
      return String(props._ganar_mas_customer_id || '').trim();
    }

    function isCurrentCustomerGanarMasCartItem(item) {
      if (!isGanarMasCartItem(item)) return false;
      return (
        !!CUSTOMERID &&
        getGanarMasCartItemCustomerId(item) === String(CUSTOMERID)
      );
    }

    function isForeignGanarMasCartItem(item) {
      return isGanarMasCartItem(item) && !isCurrentCustomerGanarMasCartItem(item);
    }

    function getCurrentCustomerGanarMasCartItems(cart) {
      return (cart?.items || []).filter(isCurrentCustomerGanarMasCartItem);
    }

    async function notifyQuickOrderCartSynced() {
      if (!isQuickOrder) return;

      try {
        const cart = await fetchCartData();
        document.dispatchEvent(new CustomEvent('ganarMas:cartSynced', {
          detail: { cart },
          bubbles: true
        }));
      } catch(e) {
        document.dispatchEvent(new CustomEvent('ganarMas:cartSynced', {
          detail: {},
          bubbles: true
        }));
      }
    }

    // Estado de comisión por línea (key -> 'true' | 'false' | 'unknown') que
    // dibuja Liquid en cart-line-items (etiqueta del producto o del padre del set).
    async function fetchCartCommissionStates() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?section_id=api-cart-items-readonly`, {
        signal: AbortSignal.timeout(20000)
      });
      if (!response.ok) throw new Error(`Ganar Mas cart commission tags failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const states = new Map();
      doc.querySelectorAll('[data-ganar-mas-line-key]').forEach(el => {
        states.set(el.dataset.ganarMasLineKey, el.dataset.ganarMasExcluded);
      });
      return states;
    }

    // Monto Ganar Más: subtotal sin Ganar Más ni no comisionables. Si no se
    // conoce la etiqueta de una línea, la línea cuenta: nunca se bloquea el pago
    // por una regla de Ganar Más.
    async function getCartAmount(cart) {
      let commissionStates = new Map();
      try {
        commissionStates = await fetchCartCommissionStates();
      } catch (err) {
        console.warn('[GANAMAS] Commission tags unavailable; all cart lines count for Ganar Mas', err);
      }
      const excludedSubtotal = (cart?.items || []).reduce((sum, item) => {
        const linePrice = Number(item.final_line_price || item.line_price || 0) || 0;
        if (isGanarMasCartItem(item)) return sum + linePrice;
        const state = commissionStates.get(item.key);
        if (state !== 'true' && state !== 'false') {
          console.warn('[GANAMAS] Commission tags unknown; cart line counts for Ganar Mas', item.key);
        }
        return state === 'true' ? sum + linePrice : sum;
      }, 0);
      const subtotal = Number(cart.items_subtotal_price || 0) - excludedSubtotal;
      return Math.max(subtotal, 0) / 100;
    }

    function getCartAttemptId(cart) {
      return String(cart?.token || '').trim();
    }

    function getOmittedAttemptStorageKey(customerId) {
      return `ganarMas:omitted:${String(customerId || '').trim()}`;
    }

    function getOmittedAttempt(customerId) {
      try {
        return sessionStorage.getItem(getOmittedAttemptStorageKey(customerId)) || '';
      } catch (err) {
        return '';
      }
    }

    function setOmittedAttempt(customerId, attemptId) {
      if (!customerId || !attemptId) return;
      try {
        sessionStorage.setItem(getOmittedAttemptStorageKey(customerId), attemptId);
      } catch (err) {}
    }

    function clearOmittedAttempt(customerId) {
      if (!customerId) return;
      try {
        sessionStorage.removeItem(getOmittedAttemptStorageKey(customerId));
      } catch (err) {}
    }

    async function fetchEligibility(week, year, cartAmount) {
      const payload = {
        week,
        year,
        source: 'first',
        customers: [{
          customerId: CUSTOMERID,
          cartAmount: `${cartAmount}`
        }]
      };
      const url = `${AWS_API_URL}/Prod/ganar-mas/calculate`;
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Eligibility fetch failed: ' + res.status + ' - ' + txt);
      }
      return await res.json();
    }

    async function postSelection(payload) {
      const url = `${AWS_API_URL}/Prod/ganar-mas`;
      try {
        const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!res.ok) {
          const txt = await res.text();
          console.warn('postSelection warning', res.status, txt);
          return null;
        }
        return await res.json();
      } catch(e) {
        console.warn('postSelection error', e);
        return null;
      }
    }

    async function fetchGanaMasProductsForUser(customerId, level, weeksWithSales) {
      const url = `${AWS_API_URL}/Prod/ganar-mas/products/${encodeURIComponent(customerId)}?level=${level}&weeksWithSales=${weeksWithSales}`;
      const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Products fetch failed: ' + res.status + ' - ' + txt);
      }
      return await res.json();
    }

    // Regalos que corresponden al beneficio según /ganar-mas/products (vigencia y
    // productos). undefined = no se pudo saber (se conservan los regalos ligados);
    // [] = sin regalos vigentes; [productId, ...] = regalos vigentes.
    async function resolveRegalosForBenefit(level, weeksWithSales) {
      try {
        const res = await fetchGanaMasProductsForUser(CUSTOMERID, level, weeksWithSales);
        if (Array.isArray(res) || !res || !Array.isArray(res.regalo)) return undefined;
        return res.regalo.map(product => extractNumericId(product?.id)).filter(Boolean);
      } catch (err) {
        safeLog('Ganar Mas regalo lookup failed, keeping linked regalos', { message: err?.message });
        return undefined;
      }
    }

    async function loadAvailableGanarMasProducts(customerId, level, weeksWithSales, force) {
      const key = `${customerId}:${level}:${weeksWithSales}`;
      if (!force && availableGanarMasProductsKey === key) return availableGanarMasProducts;

      const res = await fetchGanaMasProductsForUser(customerId, level, weeksWithSales);
      const productList = Array.isArray(res) ? res : (res?.products || []);
      const regaloList = Array.isArray(res?.regalo) ? res.regalo : [];

      availableGanarMasProducts = normalizeAvailableProducts(productList);
      availableRegaloProducts = normalizeAvailableProducts(regaloList);
      availableGanarMasProductsKey = key;
      safeLog('Available Ganar Mas products loaded', {
        customerId,
        level,
        weeksWithSales,
        count: availableGanarMasProducts.length,
        regaloCount: availableRegaloProducts.length
      });

      return availableGanarMasProducts;
    }

    async function addGanarMasProductToCart(selection, payload) {
      const productId = extractNumericId(selection?.product?.id);
      const variantId = extractNumericId(selection?.variant?.id);

      if (!productId || !variantId) {
        throw new Error('Selected Ganar Mas product does not have a valid product or variant id');
      }

      const items = [{
        id: variantId,
        quantity: 1,
        properties: {
          _ganar_mas: 'true',
          _ganar_mas_customer_id: `${CUSTOMERID}`,
          _ganar_mas_product_id: productId,
          _ganar_mas_level: `${payload.level}`,
          _ganar_mas_weeks_with_sales: `${payload.weeksWithSales}`,
          _ganar_mas_week: `${payload.week}`,
          _ganar_mas_year: `${payload.year}`,
          _ganar_mas_sales_amount: `${payload.salesAmount}`
        }
      }];

      // Los regalos salen del catálogo Ganar Más (/ganar-mas/products): una línea por
      // producto regalo con variante disponible, todas ligadas al principal.
      const regaloItems = [];
      const regaloProductIds = new Set();
      availableRegaloProducts.forEach(regaloSelection => {
        const regaloProductId = extractNumericId(regaloSelection?.product?.id);
        const regaloVariantId = extractNumericId(regaloSelection?.variant?.id);
        if (!regaloProductId || !regaloVariantId || regaloProductIds.has(regaloProductId)) return;
        regaloProductIds.add(regaloProductId);
        regaloItems.push({
          id: regaloVariantId,
          quantity: 1,
          properties: {
            _ganar_mas_regalo: 'true',
            _ganar_mas_regalo_of: productId,
            _ganar_mas_customer_id: `${CUSTOMERID}`,
            _ganar_mas_product_id: regaloProductId,
            _ganar_mas_level: `${payload.level}`,
            _ganar_mas_weeks_with_sales: `${payload.weeksWithSales}`,
            _ganar_mas_week: `${payload.week}`,
            _ganar_mas_year: `${payload.year}`
          }
        });
      });

      const addItems = list => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: list })
      });

      let res = await addItems(items.concat(regaloItems));
      let addedRegalos = regaloItems.map(item => item.properties._ganar_mas_product_id);

      // Si algún regalo no se puede agregar (sin inventario o no disponible), el
      // cliente no debe perder su Ganar Más: se agrega el principal y luego cada
      // regalo por separado; los que fallen se omiten.
      if (!res.ok && regaloItems.length > 0) {
        const txt = await res.text();
        safeLog('Ganar Mas regalos could not be added together, adding main product first', {
          status: res.status,
          response: txt,
          regalos: addedRegalos
        });
        res = await addItems(items);
        addedRegalos = [];
        if (res.ok) {
          for (const regaloItem of regaloItems) {
            const regaloRes = await addItems([regaloItem]);
            if (regaloRes.ok) {
              addedRegalos.push(regaloItem.properties._ganar_mas_product_id);
            } else {
              safeLog('Ganar Mas regalo skipped', { productId: regaloItem.properties._ganar_mas_product_id, status: regaloRes.status });
            }
          }
        }
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Ganar Mas cart add failed: ' + res.status + ' - ' + txt);
      }

      safeLog('Ganar Mas product added to cart', {
        productId,
        variantId,
        level: payload.level,
        weeksWithSales: payload.weeksWithSales
      });

      return {
        productId,
        variantId,
        regalos: addedRegalos,
        response: await res.json()
      };
    }

    async function removeGanarMasCartItems(cartData, keepKey, filterFn) {
      const cart = cartData || await fetchCartData();
      const shouldRemove = typeof filterFn === 'function' ? filterFn : isGanarMasCartItem;
      const items = (cart?.items || []).filter(item => {
        return shouldRemove(item) && (!keepKey || item.key !== keepKey);
      });

      if (!items.length) return 0;

      for (const item of items) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error('Ganar Mas cart remove failed: ' + res.status + ' - ' + txt);
        }
      }

      safeLog('Ganar Mas products removed from cart', { count: items.length, kept: keepKey || null });
      return items.length;
    }

    async function removeCurrentCustomerGanarMasCartItems(cartData, keepKey) {
      const removed = await removeGanarMasCartItems(cartData, keepKey, isCurrentCustomerGanarMasCartItem);
      if (removed) await notifyQuickOrderCartSynced();
      return removed;
    }

    async function removeForeignGanarMasCartItems(cartData) {
      const removed = await removeGanarMasCartItems(
        cartData,
        null,
        isForeignGanarMasCartItem
      );
      if (removed) await notifyQuickOrderCartSynced();
      return removed;
    }

    function hasPendingGanarMasOrderProduct() {
      const orders = Array.isArray(ganamasEligibility?.pendingGanarMasOrders)
        ? ganamasEligibility.pendingGanarMasOrders
        : [];

      return orders.some(order => {
        const lineItems = Array.isArray(order.lineItems) ? order.lineItems : [];
        return lineItems.length > 0;
      });
    }

    function getGanarMasCartItemNumber(item, property) {
      const value = Number(item?.properties?.[property]);
      return Number.isFinite(value) ? value : 0;
    }

    function getExpectedGanarMasSalesAmount(ganarArr) {
      const value = Number(ganarArr?.salesAmount || 0);
      return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
    }

    function getPayloadGanarMasSalesAmount(payload) {
      const value = Number(payload?.salesAmount || 0);
      return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
    }

    function hasCurrentGanarMasSalesAmount(item, expectedAmount) {
      const currentAmount = Number(item?.properties?._ganar_mas_sales_amount || 0);
      if (!Number.isFinite(currentAmount)) return false;
      return Number(currentAmount.toFixed(2)) === Number(expectedAmount || 0);
    }

    async function updateGanarMasCartItemSalesAmount(item, salesAmount) {
      if (!item?.key || !salesAmount) return false;

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity || 1,
          properties: {
            ...(item.properties || {}),
            _ganar_mas_sales_amount: `${salesAmount}`
          }
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Ganar Mas cart properties update failed: ' + res.status + ' - ' + txt);
      }

      safeLog('Ganar Mas cart sales amount updated', {
        key: item.key,
        salesAmount
      });
      return true;
    }

    function isAllowedGanarMasCartItem(item, ganarArr, period) {
      const assignedLevel = getGanarMasCartItemNumber(item, '_ganar_mas_level');
      const allowedLevel = Number(ganarArr?.level) || 0;
      const assignedWeeksWithSales = getGanarMasCartItemNumber(
        item,
        '_ganar_mas_weeks_with_sales'
      );
      const allowedWeeksWithSales = Number(ganarArr?.weeksWithSales) || 0;
      const assignedWeek = getGanarMasCartItemNumber(item, '_ganar_mas_week');
      const assignedYear = getGanarMasCartItemNumber(item, '_ganar_mas_year');

      return (
        assignedLevel > 0 &&
        allowedLevel > 0 &&
        assignedLevel === allowedLevel &&
        assignedWeeksWithSales > 0 &&
        assignedWeeksWithSales === allowedWeeksWithSales &&
        assignedWeek === Number(period.week) &&
        assignedYear === Number(period.year)
      );
    }

    async function validateCurrentCartGanarMasItems(cart, ganarArr, period) {
      const currentItems = getCurrentCustomerGanarMasCartItems(cart);
      if (!currentItems.length) return { kept: false, removed: 0 };
      const expectedSalesAmount = getExpectedGanarMasSalesAmount(ganarArr);

      // La vigencia de los regalos la decide /ganar-mas/products; solo se consulta si hay regalos.
      const regalos = currentItems.some(item => String(item?.properties?._ganar_mas_regalo) === 'true')
        ? await resolveRegalosForBenefit(ganarArr?.level, ganarArr?.weeksWithSales)
        : undefined;
      const validatedCart = await sanitizeGanarMasCart({
        customerId: CUSTOMERID,
        allowPrimary: item => isAllowedGanarMasCartItem(item, ganarArr, period),
        regalos
      });
      const keptItems = getCurrentCustomerGanarMasCartItems(validatedCart);
      const itemToKeep = keptItems.find(item => !isRegaloItem(item));
      const removed = currentItems.length - keptItems.length;
      let updated = false;

      if (
        itemToKeep &&
        expectedSalesAmount > 0 &&
        !hasCurrentGanarMasSalesAmount(itemToKeep, expectedSalesAmount)
      ) {
        updated = await updateGanarMasCartItemSalesAmount(itemToKeep, expectedSalesAmount);
      }

      safeLog('Ganar Mas cart products validated', {
        found: currentItems.length,
        kept: itemToKeep?.key || null,
        removed,
        updated,
        allowedLevel: Number(ganarArr?.level) || 0,
        allowedWeeksWithSales: Number(ganarArr?.weeksWithSales) || 0,
        salesAmount: expectedSalesAmount
      });

      return {
        kept: !!itemToKeep,
        removed
      };
    }

    function isSameGanarMasBenefit(item, payload) {
      const props = item?.properties || {};
      return (
        String(props._ganar_mas_level || '') === String(payload.level) &&
        String(props._ganar_mas_weeks_with_sales || '') === String(payload.weeksWithSales) &&
        String(props._ganar_mas_week || '') === String(payload.week) &&
        String(props._ganar_mas_year || '') === String(payload.year)
      );
    }

    async function syncGanarMasSelectionToCart(payload, forceReplace) {
      if (ganarMasSyncInProgress) {
        throw new Error('Ganar Mas cart sync already in progress');
      }

      ganarMasSyncInProgress = true;

      try {
        const cart = await sanitizeGanarMasCart({ customerId: CUSTOMERID });
        const currentItems = getCurrentCustomerGanarMasCartItems(cart);
        const currentItem = currentItems.find(item => !isRegaloItem(item));
        const currentProductId = currentItem?.properties?._ganar_mas_product_id || '';

        if (currentItem && !forceReplace && isSameGanarMasBenefit(currentItem, payload) && currentProductId) {
          await validateCurrentCartGanarMasItems(cart, payload, payload);

          const expectedSalesAmount = getPayloadGanarMasSalesAmount(payload);
          if (
            expectedSalesAmount > 0 &&
            !hasCurrentGanarMasSalesAmount(currentItem, expectedSalesAmount)
          ) {
            await updateGanarMasCartItemSalesAmount(currentItem, expectedSalesAmount);
          }

          safeLog('Ganar Mas cart product kept', {
            productId: currentProductId,
            level: payload.level,
            weeksWithSales: payload.weeksWithSales,
            week: payload.week,
            year: payload.year,
            salesAmount: expectedSalesAmount
          });
          return {
            productId: String(currentProductId),
            kept: true
          };
        }

        if (currentItems.length) {
          await removeCurrentCustomerGanarMasCartItems(cart);
        }

        await loadAvailableGanarMasProducts(CUSTOMERID, payload.level, payload.weeksWithSales, true);
        const selected = pickRandomGanarMasProduct();
        const added = await addGanarMasProductToCart(selected, payload);
        await sanitizeGanarMasCart({ customerId: CUSTOMERID, regalos: added.regalos });
        await notifyQuickOrderCartSynced();
        return added;
      } finally {
        ganarMasSyncInProgress = false;
      }
    }

    async function onConfirmGanamas() {
      if (!confirmBtn || confirmBtn.disabled) return;
      confirmBtn.disabled = true;

      try {
        const active = choices.find(c => c.classList.contains('choice-active'));
        const choiceVal = active ? active.getAttribute('data-choice') : '';
        if (!choiceVal) {
          confirmBtn.disabled = false;
          return;
        }

        const payload = buildGanamasSelectionPayload('pending', 'first');

        if (choiceVal === 'none') {
          payload.productId = '';
          payload.status = 'omitted';
          await removeCurrentCustomerGanarMasCartItems();
          await postSelection([payload]).catch(() => {});
          setOmittedAttempt(CUSTOMERID, currentCartAttemptId);
          confirmFlow();
          return;
        }

        if (choiceVal === 'random') {
          const added = await syncGanarMasSelectionToCart(payload, true);

          payload.productId = added.productId;
          // Solo los regalos que sí se pudieron agregar al carrito.
          payload.regaloId = (added.regalos || []).join(',');
          payload.status = 'select';
          await postSelection([payload]).catch(() => {});
          clearOmittedAttempt(CUSTOMERID);
          confirmFlow();
          return;
        }

        confirmBtn.disabled = false;
      } catch(err) {
        console.error('Error en onConfirmGanamas:', err);
        alert('Ocurrió un error, intenta nuevamente.');
        confirmBtn.disabled = false;
      }
    }

    async function onCheckoutClick(e) {
      if (bypassSubmit || checkoutLoading) return;
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      CUSTOMERID = isQuickOrder ? resolveQuickOrderCustomerId(config) : (config.customerId || null);
      setCheckoutLoadingState(true);

      try {
        await window.RegalonCheckout?.prepare({ customerId: CUSTOMERID });
        const cart = await sanitizeGanarMasCart({ customerId: CUSTOMERID || '' });
        if (!CUSTOMERID) {
          confirmFlow();
          return;
        }
        currentCartAttemptId = getCartAttemptId(cart);
        const cartAmount = await getCartAmount(cart);
        const prev = getCurrentWeek(new Date());
        const eligibility = await fetchEligibility(prev.week, prev.year, cartAmount);
        ganamasEligibility = eligibility && eligibility.length > 0 ? eligibility[0].response : null;
        const ganarArr = ganamasEligibility?.ganarMas?.[0] ?? {};
        if (
          ganamasEligibility?.currentSelectionStatus === 'omitted' &&
          getOmittedAttempt(CUSTOMERID) !== currentCartAttemptId
        ) {
          ganamasEligibility.currentSelectionStatus = 'none';
          ganarArr.selectedProduct = [];
        }
        const hasExistingOrderBenefit =
          ganamasEligibility?.currentSelectionStatus === 'sent' ||
          hasPendingGanarMasOrderProduct();

        if (hasExistingOrderBenefit) {
          await removeCurrentCustomerGanarMasCartItems();
          confirmFlow();
          return;
        }

        if (!ganamasEligibility?.eligible) {
          const payload = buildGanamasSelectionPayload('omitted', 'eligibility-sync');
          payload.productId = '';
          await removeCurrentCustomerGanarMasCartItems();
          await postSelection([payload]).catch(() => {});
          confirmFlow();
          return;
        }

        const cartValidation = await validateCurrentCartGanarMasItems(
          cart,
          ganarArr,
          prev
        );
        if (cartValidation.kept) {
          confirmFlow();
          return;
        }

        if (cartValidation.removed > 0) {
          ganamasEligibility.currentSelectionStatus = 'none';
          ganarArr.selectedProduct = [];
          await prepareAndOpenGanamasModal(ganarArr);
          return;
        }

        if (needsGiftModal(ganamasEligibility, {
          ...ganarArr,
          selectedProduct: []
        })) {
          await prepareAndOpenGanamasModal(ganarArr);
          return;
        }

        confirmFlow();
      } catch(err) {
        console.error('Error checking Ganamas eligibility:', err);
        setCheckoutLoadingState(false);
        alert('Ocurrió un error actualizando tu producto Ganar Más, intenta nuevamente.');
        if (isQuickOrder) document.dispatchEvent(new CustomEvent('ganarMas:validationFailed'));
      }
    }

    choices.forEach(choice => {
      choice.addEventListener('click', function() {
        choices.forEach(x => x.classList.remove('choice-active'));
        this.classList.add('choice-active');

        const choiceVal = this.getAttribute('data-choice');
        const container = modal.querySelector(listSelector);

        if (choiceVal === 'random') {
          if (chooseTitle) chooseTitle.classList.add('d-none');
          //if (container) container.innerHTML = '<div style="padding:20px; text-align:center">Se seleccionará un producto aleatorio.</div>';
          if (confirmBtn) confirmBtn.disabled = false;
          return;
        }

        if (chooseTitle) chooseTitle.classList.add('d-none');
        //if (container) container.innerHTML = '<div style="padding:20px; text-align:center">No se añadirá ningún producto.</div>';
        if (confirmBtn) confirmBtn.disabled = false;
      });
    });

    function cancelFlow() {
      removeHiddenCheckout();
      bypassSubmit = false;
      setCheckoutLoadingState(false);
      closeGanamasModal();
      if (checkoutBtn) checkoutBtn.focus();
    }

    if (backdrop) {
      backdrop.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
      });
    }

    checkoutButtons.forEach(button => {
      if (button.__ganamasBound) return;
      button.addEventListener('click', onCheckoutClick, { capture: true });
      button.__ganamasBound = true;
    });

    if (confirmBtn && !confirmBtn.__ganamasConfirmBound) {
      confirmBtn.addEventListener('click', onConfirmGanamas);
      confirmBtn.__ganamasConfirmBound = true;
    }

    if (form && !form.__ganamasSubmitBound) {
      form.__ganarMasCheckout = onCheckoutClick;
      form.addEventListener('submit', function(e) {
        if (bypassSubmit) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        onCheckoutClick(e);
      }, { capture: true });
      form.__ganamasSubmitBound = true;
    }

    if (isQuickOrder && !root.__ganamasQuickOrderBound) {
      window.__ganarMasQuickOrderReady = true;
      document.addEventListener('ganarMas:calculate', function(e) {
        CUSTOMERID = resolveQuickOrderCustomerId(config);
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        onCheckoutClick(e);
      });
      root.__ganamasQuickOrderBound = true;
    }

    document.addEventListener('ganarMas:orderCompleted', function(e) {
      const completedCustomerId = String(e?.detail?.customerId || CUSTOMERID || '').trim();
      clearOmittedAttempt(completedCustomerId);
    });

    document.addEventListener('keydown', function(e) {
      if (!activeModalKind) return;
      if (e.key === 'Escape') {
        cancelFlow();
      }
    });

    safeLog('Ganamas modal script initialized', { isQuickOrder: isQuickOrder });
  }

  function initAll() {
    document.querySelectorAll('[data-ganamas-root]').forEach(initRoot);
  }

  window.__initGanarMas = initAll;
  document.addEventListener('theme:cart:reload', () => setTimeout(initAll, 0));
  initAll();
})();
